import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link, useParams, useHistory } from 'react-router-dom';
import { Button, Container, Row, Col, CardHeader, Form, FormGroup, Label, Input } from 'reactstrap';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ApiClient from '../BaseUrl/ApiClient';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Cropper from 'react-easy-crop';
import { getDLDealCroppedImg } from '../../Components/Common/DealCropDL';
import './Blogs.css';

function EditBlog() {
  const { blogId } = useParams();
  const history = useHistory();
  const fileInputRef = useRef(null);
  const quillRef = useRef(null);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [content, setContent] = useState('');
  const [featuredImage, setFeaturedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [originalImage, setOriginalImage] = useState(null);
  const [removeFeaturedImage, setRemoveFeaturedImage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedFileName, setSelectedFileName] = useState('No file chosen');

  // Cropper states
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);

  // 3:1 aspect ratio configuration
  const CROP_ASPECT_RATIO = 3 / 1; // 3:1 ratio
  const CROP_WIDTH = 1104; // Final output width
  const CROP_HEIGHT = 368; // 1104 / 3 = 368 (adjusted for exact 3:1)
  
  // Calculate cropSize for preview based on container width
  const CONTAINER_WIDTH = 600; // Width of cropper container
  const CONTAINER_HEIGHT = CONTAINER_WIDTH / CROP_ASPECT_RATIO; // ~200px for 3:1 ratio
  const CROP_PREVIEW_SIZE = { width: CONTAINER_WIDTH, height: CONTAINER_HEIGHT };

  // Quill toolbar
  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'indent': '-1'}, { 'indent': '+1' }],
        ['blockquote'],
        ['link'],
        ['clean']
      ],
      handlers: {
        image: () => toast.info("Image upload is not allowed in the content area. Please use the Featured Image field."),
        video: () => toast.info("Video upload is not allowed."),
      }
    }
  }), []);

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'indent',
    'blockquote',
    'link'
  ];

  // Remove image/video buttons from toolbar
  useEffect(() => {
    const removeImageVideoButtons = () => {
      const toolbars = document.querySelectorAll('.ql-toolbar');
      toolbars.forEach(toolbar => {
        const imageButton = toolbar.querySelector('.ql-image');
        const videoButton = toolbar.querySelector('.ql-video');
        if (imageButton) imageButton.style.display = 'none';
        if (videoButton) videoButton.style.display = 'none';
      });
    };
    
    removeImageVideoButtons();
    const timer = setTimeout(removeImageVideoButtons, 500);
    return () => clearTimeout(timer);
  }, []);

  // Fetch blog details
  useEffect(() => {
    const fetchBlog = async () => {
      try {
        const res = await ApiClient.get(`/admin/blog/${blogId}`);
        const blog = res.data.responseData?.blog || res.data.responseData;

        if (!blog) throw new Error('Blog not found');

        setTitle(blog.title || '');
        setDescription(blog.description || '');
        setContent(blog.content || '');
        
        const imgUrl = blog.featuredImage?.url ? `${process.env.REACT_APP_LOGO_BASE_URL}${blog.featuredImage.url}` : null;
        setOriginalImage(imgUrl);
        setImagePreview(imgUrl || '');
        setRemoveFeaturedImage(false);
        setSelectedFileName(imgUrl ? 'Current image' : 'No file chosen');
      } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to fetch blog');
      } finally {
        setLoading(false);
      }
    };
    fetchBlog();
  }, [blogId]);

  const onCropComplete = useCallback((_, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleImageUpload = useCallback((e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedFileName(file.name);

      // Validate file type
      if (!file.type.match('image.*')) {
        toast.error("Please select a valid image file (JPEG, PNG, etc.)");
        e.target.value = '';
        setSelectedFileName(originalImage ? 'Current image' : 'No file chosen');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size should be less than 5MB");
        e.target.value = '';
        setSelectedFileName(originalImage ? 'Current image' : 'No file chosen');
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        setImageToCrop(event.target.result);
        setShowCropper(true);
        setZoom(1);
        setCrop({ x: 0, y: 0 });
        setCroppedAreaPixels(null);
        e.target.value = '';
      };
      
      reader.onerror = () => {
        toast.error("Error reading the image file");
        e.target.value = '';
        setSelectedFileName(originalImage ? 'Current image' : 'No file chosen');
      };
      
      reader.readAsDataURL(file);
    }
  }, [originalImage]);

  const handleCropComplete = async () => {
    if (!croppedAreaPixels) {
      toast.error('Please adjust the crop area before saving');
      return;
    }

    try {
      setShowCropper(false);


      const croppedImageBlob = await getDLDealCroppedImg(
        imageToCrop,
        croppedAreaPixels,
        CROP_WIDTH,
        CROP_HEIGHT
      );

      const fileName = `featured-image-${Date.now()}.jpg`;
      const croppedImageFile = new File([croppedImageBlob], fileName, { 
        type: 'image/jpeg' 
      });

      const previewUrl = URL.createObjectURL(croppedImageBlob);

      setFeaturedImage(croppedImageFile);
      setImagePreview(previewUrl);
      setRemoveFeaturedImage(false);
      setSelectedFileName(`${fileName} (${CROP_WIDTH}×${CROP_HEIGHT})`);
  

    } catch (e) {
      setShowCropper(false);
     
      toast.error(`Error cropping image: ${e.message || 'Unknown error'}`);
    }
  };

  const handleCancelCrop = () => {
    setShowCropper(false);
    setSelectedFileName(originalImage ? 'Current image' : 'No file chosen');
    setCroppedAreaPixels(null);
    setImageToCrop(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeImage = useCallback(() => {
    setFeaturedImage(null);
    setImagePreview('');
    setRemoveFeaturedImage(true);
    setSelectedFileName('No file chosen');
    setCroppedAreaPixels(null);
    setImageToCrop(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    
    // Clean up URL object if it's a local URL
    if (imagePreview && !originalImage) {
      URL.revokeObjectURL(imagePreview);
    }
  }, [imagePreview, originalImage]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!title.trim()) {
      return toast.error("Please enter a title for your blog post.");
    }
    if (!description.trim()) {
      return toast.error("Please enter a description for your blog post.");
    }
    if (!content || content === '<p><br></p>' || content === '') {
      return toast.error("Please enter content for your blog post.");
    }

    setSubmitting(true);

    try {
      const formData = new FormData();
      formData.append('title', title.trim());
      formData.append('description', description.trim());
      formData.append('content', content);

      if (featuredImage) {
        formData.append('featuredImage', featuredImage);
      }
      formData.append('removeFeaturedImage', removeFeaturedImage.toString());

      const response = await ApiClient.put(`/admin/blog/${blogId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response.data.responseCode === 'success' || response.status === 200) {
        toast.success('Blog updated successfully!');
        setTimeout(() => history.push('/blogs-list'), 1500);
      } else {
        throw new Error(response.data.responseMessage || "Failed to update blog");
      }
    } catch (err) {
      console.error('Submit error:', err);
      const errorMessage = err.response?.data?.responseMessage?.[0] ||
                           err.response?.data?.message ||
                           err.response?.data?.error ||
                           "Failed to update blog. Please try again.";
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center mt-5">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">Loading blog...</p>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <ToastContainer 
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      <Container fluid>
        <CardHeader className="d-flex mb-3 p-3 justify-content-between align-items-center">
          <h4 className="mb-0">Edit Blog Post</h4>
          <div className="d-flex gap-2">
            <Button 
              tag={Link} 
              to="/blogs-list" 
              color="secondary"
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={submitting} 
              color="primary"
            >
              {submitting ? 'Updating...' : 'Update'}
            </Button>
          </div>
        </CardHeader>

        <Row>
          <Col lg={12}>
            <Form onSubmit={(e) => e.preventDefault()}>
              {/* Blog Title */}
              <FormGroup className="mb-3">
                <Label for="title" className="form-label">Blog Title <span className="text-danger">*</span></Label>
                <Input 
                  type="text" 
                  id="title" 
                  className="form-control"
                  placeholder="Enter blog title"
                  value={title} 
                  onChange={(e) => setTitle(e.target.value)}
                  disabled={submitting}
                  maxLength={200}
                />
              </FormGroup>

              {/* Blog Description */}
              <FormGroup className="mb-3">
                <Label for="description" className="form-label">Blog Description <span className="text-danger">*</span></Label>
                <Input 
                  type="textarea" 
                  id="description"
                  className="form-control"
                  placeholder="Enter a short description of your blog post"
                  value={description} 
                  onChange={(e) => setDescription(e.target.value)}
                  disabled={submitting} 
                  rows="3"
                  maxLength={500}
                />
              </FormGroup>

              {/* Featured Image */}
              <FormGroup className="mb-3">
                <Label className="form-label">Featured Image <span className="text-danger">*</span></Label>
                <div className="d-flex flex-column gap-2">
                  <div className="d-flex">
                    <Button 
                      type="button" 
                      color="primary"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={submitting || showCropper}
                      className="flex-shrink-0"
                      style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                    >
                      {showCropper ? 'Cropping...' : 'Choose File'}
                    </Button>
                    <Input 
                      type="text" 
                      readOnly 
                      value={selectedFileName}
                      className="form-control ms-0"
                      style={{ 
                        flex: 1, 
                        cursor: showCropper ? "not-allowed" : "pointer", 
                        borderTopLeftRadius: 0, 
                        borderBottomLeftRadius: 0 
                      }}
                      onClick={() => !showCropper && fileInputRef.current?.click()} 
                      disabled={submitting || showCropper}
                    />
                    <input 
                      type="file" 
                      id="featuredImage" 
                      accept="image/*"
                      onChange={handleImageUpload} 
                      ref={fileInputRef}
                      style={{ display: "none" }} 
                      disabled={submitting || showCropper}
                    />
                  </div>

                  {/* Image Preview */}
                  {imagePreview && !showCropper && (
                    <div className="d-flex align-items-center">
                      <div className="position-relative me-2">
                        <img 
                          src={imagePreview} 
                          alt="Preview"
                          className="img-thumbnail"
                          style={{ 
                            width: '120px', 
                            height: '40px', // 3:1 ratio for preview
                            objectFit: 'cover',
                          
                          }} 
                        />
                        <div 
                          className="position-absolute top-0 end-0 bg-danger text-white rounded-circle d-flex align-items-center justify-content-center"
                          style={{ 
                            width: '20px', 
                            height: '20px', 
                            fontSize: '12px',
                            cursor: 'pointer',
                            zIndex: 1
                          }}
                          onClick={removeImage}
                          title="Remove image"
                        >
                          ×
                        </div>
                      </div>
                      {featuredImage && (
                        <small className="text-muted align-self-center">
                          {CROP_WIDTH}×{CROP_HEIGHT} (3:1) - New
                        </small>
                      )}
                      {originalImage && !featuredImage && (
                        <small className="text-muted align-self-center">
                          Current image - {originalImage && removeFeaturedImage ? 'Will be removed' : 'Will be kept'}
                        </small>
                      )}
                    </div>
                  )}

                

               
                </div>
              </FormGroup>

              {/* Blog Content */}
              <FormGroup className="mb-4">
                <Label className="form-label">Blog Content <span className="text-danger">*</span></Label>
                <div style={{ minHeight: '400px', marginBottom: '50px' }}>
                  <ReactQuill 
                    ref={quillRef} 
                    value={content} 
                    onChange={setContent}
                    placeholder="Write your blog content here..."
                    modules={modules} 
                    formats={formats} 
                    theme="snow"
                    style={{ 
                      height: '400px', 
                      border: '1px solid #e9ecef', 
                      borderRadius: '4px' 
                    }}
                    readOnly={submitting}
                  />
                </div>
              </FormGroup>
            </Form>
          </Col>
        </Row>
      </Container>

      {/* Cropper Modal */}
      {showCropper && (
        <div className="crop-modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '20px'
        }}>
          <div className="crop-modal-content" style={{
            background: 'white',
            borderRadius: '12px',
            width: '90%',
            maxWidth: '900px',
            maxHeight: '90vh',
            overflow: 'hidden',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)'
          }}>
            {/* Header */}
            <div className="crop-header" style={{
              padding: '20px 24px',
              borderBottom: '1px solid #e9ecef',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <h5 className="mb-1">Crop Featured Image</h5>

              </div>
              <Button 
                color="link" 
                size="sm"
                className="p-0 text-muted"
                onClick={handleCancelCrop}
                style={{ 
                  fontSize: '24px', 
                  lineHeight: '1',
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title="Close"
              >
                ×
              </Button>
            </div>
            
            {/* Crop Container - 3:1 aspect ratio */}
            <div className="crop-container" style={{
              position: 'relative',
              width: '100%',
              height: `${CONTAINER_HEIGHT}px`, // Dynamic height based on width
              minHeight: '400px',
              background: '#f8f9fa',
              border: '2px solid #e9ecef',
              borderRadius: '0 0 12px 12px',
              overflow: 'hidden'
            }}>
              <Cropper
                image={imageToCrop}
                crop={crop}
                zoom={zoom}
                aspect={CROP_ASPECT_RATIO}
                onCropChange={setCrop}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
                cropShape="rect"
                zoomWithWheel={true}
                minZoom={0.5}
                maxZoom={4}
                wheelSpeed={0.01}
                restrictPosition={true}
                cropSize={CROP_PREVIEW_SIZE}
                showGrid={true}
                objectFit="contain"
              />
            </div>
            
            {/* Controls */}
            <div className="crop-controls" style={{
              padding: '20px 24px',
              borderTop: '1px solid #e9ecef',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px'
            }}>
              {/* Zoom Control */}
              <div className="zoom-control d-flex align-items-center justify-content-between">
                <label className="mb-0 fw-medium">Zoom: {(zoom * 100).toFixed(0)}%</label>
                <div className="d-flex align-items-center gap-3 flex-grow-1">
                  <input
                    type="range"
                    value={zoom}
                    min={0.5}
                    max={4}
                    step={0.01}
                    onChange={(e) => setZoom(parseFloat(e.target.value))}
                    className="flex-grow-1"
                    style={{
                      height: '4px',
                      borderRadius: '2px',
                      background: '#e9ecef',
                      outline: 'none',
                      cursor: 'pointer'
                    }}
                  />
                  <span className="text-muted small">{zoom.toFixed(2)}x</span>
                </div>
              </div>
              

              
              {/* Action Buttons */}
              <div className="d-flex gap-2 justify-content-end">
                <Button 
                  color="secondary" 
                  onClick={handleCancelCrop}
                  disabled={submitting}
                  className="px-4"
                >
                  Cancel
                </Button>
                <Button 
                  color="primary" 
                  onClick={handleCropComplete}
                  disabled={!croppedAreaPixels || submitting}
                  className="px-4"
                >
                  {croppedAreaPixels ? 'Apply Crop' : 'Adjust Crop Area'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default React.memo(EditBlog);